import { useState, useEffect } from "react";
import { AppHeader } from "@/components/AppHeader";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import SportBadge from "@/components/SportBadge";
import { Search, Clock, Trophy } from "lucide-react";

interface Matchup {
  id: string;
  league_id: string;
  gameweek: number;
  home_team_id: string;
  away_team_id: string;
  status: string; // Allow any string instead of strict union
  home_points: number;
  away_points: number;
  projected_home: number;
  projected_away: number;
  match_id?: string;
  league?: {
    name: string;
    sport: 'soccer' | 'csgo';
  };
  match?: {
    starts_at: string;
    home_score: number;
    away_score: number;
  };
}

const Matches = () => {
  const { user } = useAuth();
  const [matchups, setMatchups] = useState<Matchup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGameweek, setSelectedGameweek] = useState<string>("");
  const [sportFilter, setSportFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (user) {
      fetchMatchups();
    }
  }, [user, selectedGameweek, sportFilter, statusFilter]);

  const fetchMatchups = async () => {
    if (!user) return;

    try {
      setLoading(true);

      let query = supabase
        .from('matchups')
        .select(`
          *,
          leagues!matchups_league_id_fkey(name, sport),
          home_team:league_players!matchups_home_team_id_fkey(
            profiles!league_players_user_id_fkey(display_name, username)
          ),
          away_team:league_players!matchups_away_team_id_fkey(
            profiles!league_players_user_id_fkey(display_name, username)
          ),
          matches(starts_at, home_score, away_score)
        `)
;

      if (selectedGameweek && selectedGameweek !== "all") {
        query = query.eq('gameweek', parseInt(selectedGameweek));
      }

      if (statusFilter !== "all") {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query.order('gameweek', { ascending: false });

      if (error) throw error;

      let filteredData = data || [];

      // Filter by sport
      if (sportFilter !== "all") {
        filteredData = filteredData.filter(matchup => 
          (matchup as any).league?.sport === sportFilter
        );
      }

      // Filter by search query
      if (searchQuery) {
        filteredData = filteredData.filter(matchup =>
          (matchup as any).league?.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      setMatchups(filteredData);
    } catch (error) {
      console.error('Error fetching matchups:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <Badge className="bg-red-100 text-red-800 animate-pulse">Live</Badge>;
      case 'final':
        return <Badge className="bg-gray-100 text-gray-800">Final</Badge>;
      case 'upcoming':
        return <Badge className="bg-blue-100 text-blue-800">Upcoming</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return "TBD";
    return new Date(dateString).toLocaleString();
  };

  const getScore = (matchup: Matchup) => {
    if (matchup.status === 'final' || matchup.status === 'live') {
      return `${matchup.home_points.toFixed(1)} - ${matchup.away_points.toFixed(1)}`;
    }
    return `${matchup.projected_home.toFixed(1)} - ${matchup.projected_away.toFixed(1)}`;
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <AppHeader />
          <div className="p-6 space-y-6">
            <Skeleton className="h-8 w-48" />
            <div className="grid gap-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <AppHeader />
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Matches</h1>
          </div>

          {/* Top Controls */}
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={selectedGameweek} onValueChange={setSelectedGameweek}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Gameweeks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Gameweeks</SelectItem>
                {[1, 2, 3, 4, 5].map(gw => (
                  <SelectItem key={gw} value={gw.toString()}>Gameweek {gw}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sportFilter} onValueChange={setSportFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Sport" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sports</SelectItem>
                <SelectItem value="soccer">Soccer</SelectItem>
                <SelectItem value="csgo">CS:GO</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="final">Final</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                placeholder="Search leagues or opponents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Matchup Cards */}
          <div className="grid gap-4">
            {matchups.length === 0 ? (
              <Card>
                <CardContent className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <Trophy className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold text-muted-foreground">No matches found</h3>
                    <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              matchups.map((matchup) => (
                <Card key={matchup.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {(matchup as any).league && (
                        <SportBadge sport={(matchup as any).league.sport} />
                      )}
                      <Badge variant="outline" className="text-xs">
                        {(matchup as any).league?.name}
                      </Badge>
                    </div>
                      {getStatusBadge(matchup.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                      {/* Teams */}
                      <div className="text-center">
                        <div className="font-semibold">
                          Home Team
                        </div>
                        <div className="text-sm text-muted-foreground">vs</div>
                        <div className="font-semibold">
                          Away Team
                        </div>
                      </div>

                      {/* Score */}
                      <div className="text-center">
                        <div className="text-2xl font-mono font-bold">
                          {getScore(matchup)}
                        </div>
                        {matchup.status === 'upcoming' && (
                          <div className="text-sm text-muted-foreground">Projected</div>
                        )}
                      </div>

                      {/* Time & Action */}
                      <div className="text-center space-y-2">
                        <div className="flex items-center justify-center gap-1 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {formatTime(matchup.match?.starts_at)}
                        </div>
                        <Button size="sm" variant="outline">
                          Open Match Center
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default Matches;